import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useProfile } from "@/hooks/useProfile";
import {
  DEFAULT_ROLE_PERMISSIONS,
  isAdminRole,
  type AdminModule,
  type AdminRole,
} from "@/lib/adminModules";

type PermissionMatrix = Partial<Record<AdminRole, Partial<Record<AdminModule, boolean>>>>;
type OverrideMap = Partial<Record<AdminModule, boolean>>;

interface UsePermissionsResult {
  role: string;
  loading: boolean;
  hasAdminAccess: boolean;
  isSuperAdmin: boolean;
  can: (module: AdminModule) => boolean;
  reload: () => void;
}

/**
 * 读取当前用户的角色与权限矩阵，判断其能否访问某个后台模块。
 * 判断顺序：超管（admin）始终全量 → 个人覆盖（user_permissions）→ 角色矩阵（role_permissions）→ 默认值。
 */
export function usePermissions(): UsePermissionsResult {
  const { profile, loading: profileLoading } = useProfile();
  const [matrix, setMatrix] = useState<PermissionMatrix>({});
  const [overrides, setOverrides] = useState<OverrideMap>({});
  const [loading, setLoading] = useState(true);

  const role = profile?.role ?? "customer";
  const userId = profile?.id ?? null;
  const hasAdminAccess = isAdminRole(role);
  const isSuperAdmin = role === "admin";

  const load = useCallback(async () => {
    if (!hasAdminAccess) {
      setMatrix({});
      setOverrides({});
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [permRes, overrideRes] = await Promise.all([
        supabase.from("role_permissions").select("role, module, allowed"),
        userId
          ? supabase.from("user_permissions").select("module, allowed").eq("user_id", userId)
          : Promise.resolve({ data: [] as { module: string; allowed: boolean }[] }),
      ]);
      const next: PermissionMatrix = {};
      (permRes.data ?? []).forEach((row) => {
        const r = row.role as AdminRole;
        if (!isAdminRole(r)) return;
        if (!next[r]) next[r] = {};
        next[r]![row.module as AdminModule] = row.allowed === true;
      });
      setMatrix(next);

      const nextOverrides: OverrideMap = {};
      ((overrideRes.data ?? []) as { module: string; allowed: boolean }[]).forEach((row) => {
        nextOverrides[row.module as AdminModule] = row.allowed === true;
      });
      setOverrides(nextOverrides);
    } catch {
      setMatrix({});
      setOverrides({});
    } finally {
      setLoading(false);
    }
  }, [hasAdminAccess, userId]);

  useEffect(() => {
    void load();
  }, [load]);

  const can = useCallback(
    (module: AdminModule): boolean => {
      if (isSuperAdmin) return true;
      if (!hasAdminAccess) return false;
      // 个人覆盖优先于角色级矩阵。
      if (typeof overrides[module] === "boolean") return overrides[module] as boolean;
      const roleMatrix = matrix[role as AdminRole];
      if (roleMatrix && typeof roleMatrix[module] === "boolean") {
        return roleMatrix[module] as boolean;
      }
      return DEFAULT_ROLE_PERMISSIONS[role as AdminRole]?.[module] ?? false;
    },
    [isSuperAdmin, hasAdminAccess, role, matrix, overrides],
  );

  return {
    role,
    loading: loading || profileLoading,
    hasAdminAccess,
    isSuperAdmin,
    can,
    reload: load,
  };
}